import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import { Drawer, Select, TextField } from "@outpave/ds";
import { toast } from "react-toastify";
import ApiKeyRole, {
  API_KEY_ROLE_OPTIONS,
  API_KEY_DEFAULT_LIMITS,
} from "enums/ApiKeyRole";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { ApiKey, UpdateApiKeyInput } from "hooks/useApiKeys";
import { toastMutationError } from "utils/toastMutationError";
import { Inline } from "components/layout/reusableLayouts";
import { canonicalizeIpOrCidr } from "utils/apiKeys/ipAllowlist";
import { apiKeyRoleLabelKey } from "utils/apiKeys/apiKeyStatus";
import IpAllowlistField, { AllowListMode } from "./IpAllowlistField";

interface ApiKeyEditDrawerProps {
  open: boolean;
  apiKey: ApiKey | null;
  onClose: () => void;
  onUpdate: (id: string, input: UpdateApiKeyInput) => Promise<ApiKey>;
}

/** Each of the two rate limit fields takes half the row and may shrink. */
const half = { flex: 1, minWidth: 0 } as const;

/**
 * A rate limit is a whole number of requests. GraphQL Int takes a negative one
 * happily and it becomes the limiter's max, so the form refuses it here.
 */
const isWholeCount = (value: string): boolean => {
  if (!value) return true;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0;
};

/**
 * A value that differs from the role default is an override. A value EQUAL to
 * it is ambiguous, because the form prefills a stored null with that same
 * number, so the STORED value breaks the tie: a key pinned to it stays pinned,
 * and one that was tracking the default keeps tracking.
 */
const resolveLimit = (
  value: string,
  stored: number | null,
  roleDefault: number,
): number | null => {
  if (!value) return null;
  const entered = Number(value);
  if (entered !== roleDefault) return entered;
  return stored === roleDefault ? stored : null;
};

const toDateInputValue = (epochMs?: number | null): string => {
  if (!epochMs) return "";
  const d = new Date(epochMs);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const ApiKeyEditDrawer: React.FC<ApiKeyEditDrawerProps> = ({
  open,
  apiKey,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation();
  // PlatformOwner, to match assertCanSetRateLimits in the ApiKey resolver.
  const canEditRateLimits = useCheckUserRole(Role.PlatformOwner);

  const [name, setName] = useState("");
  const [role, setRole] = useState<ApiKeyRole>(ApiKeyRole.ReadOnly);
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [allowListMode, setAllowListMode] = useState<AllowListMode>("ALL");
  const [allowListIps, setAllowListIps] = useState<string[]>([]);
  const [allowListIpDraft, setAllowListIpDraft] = useState("");
  const [rateLimitSustained, setRateLimitSustained] = useState<string>("");
  const [rateLimitBurst, setRateLimitBurst] = useState<string>("");
  // What the key actually stored, which the prefilled fields can no longer say.
  const [storedLimits, setStoredLimits] = useState<{
    sustained: number | null;
    burst: number | null;
  }>({ sustained: null, burst: null });
  const [submitting, setSubmitting] = useState(false);

  const roleDefaults = API_KEY_DEFAULT_LIMITS[role];

  useEffect(() => {
    if (!apiKey) return;
    setName(apiKey.name || "");
    setRole((apiKey.role as ApiKeyRole) || ApiKeyRole.ReadOnly);
    setExpiresAt(toDateInputValue(apiKey.expiresAt));

    const stored = (apiKey.allowListIp || "").trim();
    if (!stored || stored.toUpperCase() === "ALL") {
      setAllowListMode("ALL");
      setAllowListIps([]);
    } else {
      setAllowListMode("RESTRICT");
      setAllowListIps(
        stored
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean)
          .map((entry) => canonicalizeIpOrCidr(entry)),
      );
    }
    setAllowListIpDraft("");

    const defaults =
      API_KEY_DEFAULT_LIMITS[
        (apiKey.role as ApiKeyRole) || ApiKeyRole.ReadOnly
      ];
    setRateLimitSustained(
      String(apiKey.rateLimitSustained ?? defaults.sustainedPerHour),
    );
    setRateLimitBurst(String(apiKey.rateLimitBurst ?? defaults.burst));
    setStoredLimits({
      sustained: apiKey.rateLimitSustained ?? null,
      burst: apiKey.rateLimitBurst ?? null,
    });
    setSubmitting(false);
  }, [apiKey, open]);

  // Changing role is a deliberate "reconsider limits" action, as it is in the
  // create drawer. Done here rather than in an effect on `role`, which would
  // also fire on the prefill and wipe a stored override.
  const handleRoleChange = (next: ApiKeyRole) => {
    setRole(next);
    const defaults = API_KEY_DEFAULT_LIMITS[next];
    setRateLimitSustained(String(defaults.sustainedPerHour));
    setRateLimitBurst(String(defaults.burst));
    // Reconsidering the limits drops what the old role had pinned, so the new
    // role's defaults track until someone overrides them again.
    setStoredLimits({ sustained: null, burst: null });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!apiKey) return;
    if (!name.trim()) {
      toast.error(t("ApiKeyNameRequired"));
      return;
    }
    if (allowListMode === "RESTRICT" && allowListIpDraft.trim()) {
      toast.error(t("ApiKeyConfirmIpEntry"));
      return;
    }
    if (allowListMode === "RESTRICT" && allowListIps.length === 0) {
      toast.error(t("ApiKeyAddAtLeastOneIp"));
      return;
    }
    if (
      canEditRateLimits &&
      (!isWholeCount(rateLimitSustained) || !isWholeCount(rateLimitBurst))
    ) {
      toast.error(t("ApiKeyRateLimitWholeNumber"));
      return;
    }

    setSubmitting(true);
    try {
      const input: UpdateApiKeyInput = {
        name: name.trim(),
        role,
        expiresAt: expiresAt ? new Date(expiresAt).getTime() : null,
        allowListIp: allowListMode === "ALL" ? "ALL" : allowListIps.join(","),
        // Same rule as the create drawer: a value matching the role default is
        // sent as null, so the key keeps tracking future default changes. The
        // form prefills a stored null with that default, so sending the number
        // back would turn every save into a permanent override.
        ...(canEditRateLimits && {
          rateLimitSustained: resolveLimit(
            rateLimitSustained,
            storedLimits.sustained,
            roleDefaults.sustainedPerHour,
          ),
          rateLimitBurst: resolveLimit(
            rateLimitBurst,
            storedLimits.burst,
            roleDefaults.burst,
          ),
        }),
      };
      await onUpdate(apiKey.id, input);
      toast.success(t("ApiKeyUpdatedToast"));
      onClose();
    } catch (err: unknown) {
      toastMutationError(err, t("ApiKeyUpdateError"));
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t("Edit API key")}
      loading={submitting}
      footer={
        <>
          <Button
            variant="primary-outlined"
            onClick={handleClose}
            disabled={submitting}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
          >
            {t("Save changes")}
          </Button>
        </>
      }
    >
      <TextField
        label={t("Name")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        disabled={submitting}
      />

      <Select
        label={t("Role")}
        value={role}
        onChange={(value) => handleRoleChange((value as ApiKeyRole) ?? role)}
        clearable={false}
        fullWidth
        disabled={submitting}
        options={API_KEY_ROLE_OPTIONS.map((r) => ({
          id: r,
          label: t(apiKeyRoleLabelKey(r)),
        }))}
      />

      <TextField
        label={t("Expires at (optional)")}
        type="date"
        value={expiresAt}
        onChange={(e) => setExpiresAt(e.target.value)}
        fullWidth
        disabled={submitting}
      />

      <IpAllowlistField
        mode={allowListMode}
        onModeChange={setAllowListMode}
        entries={allowListIps}
        onEntriesChange={setAllowListIps}
        draft={allowListIpDraft}
        onDraftChange={setAllowListIpDraft}
        disabled={submitting}
      />

      {canEditRateLimits && (
        <Inline gap="var(--space-5)" align="start" wrap={false}>
          <Box sx={half}>
            <TextField
              label={t("Sustained rate (per hour)")}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={rateLimitSustained}
              onChange={(e) => setRateLimitSustained(e.target.value)}
              fullWidth
              disabled={submitting}
            />
          </Box>
          <Box sx={half}>
            <TextField
              label={t("Burst rate (max in 10 seconds)")}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={rateLimitBurst}
              onChange={(e) => setRateLimitBurst(e.target.value)}
              fullWidth
              disabled={submitting}
            />
          </Box>
        </Inline>
      )}
    </Drawer>
  );
};

export default ApiKeyEditDrawer;
